import { useState, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { EInvoiceAddressSearchFilter } from '../models/eInvoiceAddressSearchFilter';
import { EInvoiceApplicationsDataGrid } from '../components/eInvoiceApplicationsDataGrid';

export const EInvoiceApplicationsPage = () => {
  const { refetchApplicationsBadge } = useOutletContext<{ refetchApplicationsBadge?: () => void }>();
  const isMounted = useIsMounted();
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);

  const [applicationSearchFilter, setApplicationSearchFilter] = useLocalStorage(LocalStorageKeys.EInvoiceApplicationsFilter, {
    sortCol: 'eInvoiceAddress_id',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as EInvoiceAddressSearchFilter);

  const searchFilter: EInvoiceAddressSearchFilter = useMemo(() => {
    return {
      filter: applicationSearchFilter?.filter,
      pageSize: applicationSearchFilter?.pageSize,
      pageIndex: applicationSearchFilter?.pageIndex,
      sortCol: applicationSearchFilter?.sortCol,
      sortDir: applicationSearchFilter?.sortDir,
    } as EInvoiceAddressSearchFilter;
  }, [
    applicationSearchFilter?.filter,
    applicationSearchFilter?.pageIndex,
    applicationSearchFilter?.pageSize,
    applicationSearchFilter?.sortCol,
    applicationSearchFilter?.sortDir,
  ]);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setApplicationSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setApplicationSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setApplicationSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setApplicationSearchFilter, tempSearchString]);

  return (
    <>
      <PageFilterSection
        leftContent={
          <SearchField
            initialValue={searchFilter.filter || ''}
            onSearch={(newSearchString) => {
              setSearchInitialized(true);
              setTempSearchString(newSearchString);
            }}
          />
        }
      />
      <EInvoiceApplicationsDataGrid
        searchFilter={searchFilter}
        onApplicationLinked={refetchApplicationsBadge}
        searchFilterCallback={(newFilter) =>
          setApplicationSearchFilter((prev) => ({
            ...prev,
            ...newFilter,
          }))
        }
      />
    </>
  );
};
